import React, { useState, useEffect, useCallback, useRef } from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import { toast } from "sonner";
import { RecordTimeField, EMPTY_TIME, recordTouchpointTime, timeFieldProblem } from "../components/time/RecordTimeField";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import BonsaiMailAssist from "../components/BonsaiMailAssist";
import {
  Send, Paperclip, X, Plus, Upload, Loader2,
  Reply, ReplyAll, Forward, Settings, FolderOpen,
  ChevronLeft, FileText, Image, Trash2, HardDrive
} from "lucide-react";

import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

// ═══════════════════════════════════════════════════════════════════
// SIGNATURE SETTINGS MODAL
// ═══════════════════════════════════════════════════════════════════
export function SignatureSettings({ bonsaiApi, onClose }) {
  const [signature, setSignature] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);
  const headers = (() => { const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id"); return uid ? { "X-Bonsai-User-Id": uid } : {}; })();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/bonsai/mail/signature`, { headers });
        setSignature(res.data);
        setDisclaimer(res.data.disclaimer_text || "");
        setAnnouncement(res.data.announcement_text || "");
        setEnabled(res.data.enabled !== false);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/bonsai/mail/signature/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success(`Uploaded ${res.data.filename}`);
      const sigRes = await axios.get(`${API}/bonsai/mail/signature`, { headers });
      setSignature(sigRes.data);
    } catch (e) { toast.error(errorMessage(e, "Upload failed")); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bonsai/mail/signature`, {
        disclaimer_text: disclaimer,
        announcement_text: announcement,
        enabled
      }, { headers });
      toast.success("Signature saved");
      onClose();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-teal" /></div>;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-navy border border-navy-border rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-navy-border">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Settings className="w-4 h-4 text-teal" /> Email Signature</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0 text-slate-400"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-4">
          {/* Announcement text (bold, above image) */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider block mb-1">Announcement (optional)</label>
            <p className="text-[10px] text-slate-500 mb-1.5">Bold text shown above signature image (e.g. holiday notice)</p>
            <textarea
              value={announcement}
              onChange={e => setAnnouncement(e.target.value)}
              rows={2}
              className="w-full bg-navy border border-navy-border rounded p-2 text-xs text-slate-300 resize-y focus:border-teal focus:outline-none font-bold"
              placeholder="e.g. Please note: Our office will be closed from 24 Dec - 2 Jan..."
              data-testid="announcement-input"
            />
          </div>

          {/* Signature image */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider block mb-2">Signature Image</label>
            {signature?.image_base64 ? (
              <div className="border border-navy-border rounded p-2 bg-white">
                <img src={signature.image_base64} alt="Signature" className="max-w-full h-auto" data-testid="signature-preview" />
              </div>
            ) : (
              <div className="border border-dashed border-navy-border rounded p-6 text-center text-slate-500 text-sm">No signature image uploaded</div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} className="hidden" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-2 text-xs border-teal text-teal" data-testid="upload-signature-btn">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
              {signature?.image_base64 ? "Replace Image" : "Upload Image"}
            </Button>
          </div>

          {/* Standard disclaimer text */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider block mb-1">Disclaimer Text</label>
            <p className="text-[10px] text-slate-500 mb-1.5">Standard Bonsai Law disclaimer — Calibri size 8, grey. Shown below signature image.</p>
            <textarea
              value={disclaimer}
              onChange={e => setDisclaimer(e.target.value)}
              rows={6}
              className="w-full bg-navy border border-navy-border rounded p-2 text-[11px] text-slate-400 resize-y focus:border-teal focus:outline-none"
              style={{ fontFamily: "Calibri, sans-serif" }}
              placeholder="Disclaimer text appears below your signature image..."
              data-testid="disclaimer-input"
            />
          </div>

          {/* Enable/disable */}
          <div className="flex items-center gap-2">
            <Checkbox checked={enabled} onCheckedChange={setEnabled} className="border-slate-600" data-testid="signature-enabled-check" />
            <label className="text-xs text-slate-300">Append signature to all outgoing emails</label>
          </div>

          {/* Preview */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider block mb-2">Preview</label>
            <div className="border border-navy-border rounded p-3 bg-white text-black max-h-48 overflow-y-auto" data-testid="signature-full-preview">
              {announcement && (
                <div style={{ fontWeight: "bold", fontSize: "11px", color: "#333", marginBottom: "8px", lineHeight: "1.4", fontFamily: "Calibri, sans-serif" }}>
                  {announcement.split("\n").map((line, i) => <span key={i}>{line}<br /></span>)}
                </div>
              )}
              {signature?.image_base64 && (
                <img src={signature.image_base64} alt="Sig preview" style={{ maxWidth: "100%", height: "auto" }} />
              )}
              {disclaimer && (
                <div style={{ marginTop: "12px", fontFamily: "Calibri, sans-serif", fontSize: "8pt", color: "#555", lineHeight: "1.4", maxWidth: "650px" }}>
                  {disclaimer.split("\n\n").map((para, i) => (
                    <p key={i} style={{ margin: "6px 0" }}>
                      {para.startsWith("Cybercrime Alert") ? <><b>{para.split("\n")[0]}</b><br />{para.split("\n").slice(1).join(" ")}</> : para.replace(/\n/g, " ")}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-navy-border">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-slate-400">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs bg-teal hover:bg-teal text-white" data-testid="save-signature-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save Signature
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SHAREPOINT FILE PICKER MODAL
// ═══════════════════════════════════════════════════════════════════
export function SharePointFilePicker({ bonsaiApi, onAttach, onClose, defaultMatter = null }) {
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [currentPath, setCurrentPath] = useState([]); // [{driveId, itemId, name}]
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const headers = (() => { const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id"); return uid ? { "X-Bonsai-User-Id": uid } : {}; })();
  const autoNavRef = useRef(false);

  // Auto-navigate when a default matter is provided (e.g. from filing sidebar)
  useEffect(() => {
    if (defaultMatter?.driveId && defaultMatter?.itemId && !autoNavRef.current) {
      autoNavRef.current = true;
      setSelectedMatter(defaultMatter);
      browseFolder(defaultMatter.driveId, defaultMatter.itemId, defaultMatter.reference || defaultMatter.title, true);
    }
  }, [defaultMatter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search matters
  useEffect(() => {
    if (matterSearch.length < 2) { setMatterResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/bonsai/filing/matters`, { params: { q: matterSearch }, headers });
        setMatterResults(Array.isArray(res.data) ? res.data : res.data.matters || []);
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(t);
  }, [matterSearch]);

  // Browse folder
  const browseFolder = async (driveId, itemId, name, autoNavToDocuments = false) => {
    setLoadingItems(true);
    try {
      const res = await axios.get(`${API}/bonsai/mail/sharepoint-files/${driveId}/${itemId}`, { headers });
      const fetchedItems = res.data.items || [];
      setItems(fetchedItems);
      if (name) setCurrentPath(prev => [...prev, { driveId, itemId, name }]);
      
      // Auto-navigate into docs/documents subfolder if requested (case-insensitive)
      if (autoNavToDocuments) {
        const nameLC = (i) => i.name.toLowerCase();
        const docsFolder = fetchedItems.find(i => i.is_folder && nameLC(i) === "docs")
          || fetchedItems.find(i => i.is_folder && nameLC(i).includes("document"));
        if (docsFolder) {
          // Small delay so user sees the navigation
          setTimeout(() => browseFolder(driveId, docsFolder.id, docsFolder.name), 200);
        }
      }
    } catch (e) { toast.error("Failed to browse folder"); }
    finally { setLoadingItems(false); }
  };

  const goBack = () => {
    if (currentPath.length <= 1) { setSelectedMatter(null); setCurrentPath([]); setItems([]); return; }
    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);
    const parent = newPath[newPath.length - 1];
    browseFolder(parent.driveId, parent.itemId, null).then(() => setCurrentPath(newPath));
  };

  const toggleFile = (item) => {
    setSelectedFiles(prev => {
      const exists = prev.find(f => f.id === item.id);
      if (exists) return prev.filter(f => f.id !== item.id);
      const driveId = currentPath[0]?.driveId || selectedMatter?.driveId;
      return [...prev, { ...item, driveId }];
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="sp-file-picker">
      <div className="bg-navy border border-navy-border rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-3 border-b border-navy-border">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Paperclip className="w-4 h-4 text-teal" /> Attach from SharePoint</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0 text-slate-400"><X className="w-4 h-4" /></Button>
        </div>

        <div className="p-3 flex-1 overflow-y-auto">
          {!selectedMatter ? (
            <>
              <Input placeholder="Search matters..." value={matterSearch} onChange={e => setMatterSearch(e.target.value)}
                className="h-8 text-xs border-navy-border mb-2" data-testid="sp-matter-search" />
              <div className="space-y-0.5">
                {matterResults.slice(0, 10).map(m => (
                  <button key={m.id || m.itemId} className="w-full text-left px-2 py-1.5 rounded text-xs text-slate-300 hover:bg-navy-hover"
                    onClick={() => { setSelectedMatter(m); browseFolder(m.driveId, m.itemId, m.reference || m.title, true); }}>
                    <div className="font-medium">{m.reference || m.title}</div>
                    {m.client_name && <div className="text-[10px] text-slate-500">{m.client_name}</div>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Breadcrumb */}
              <div className="flex items-center gap-1 mb-2 text-xs text-slate-400">
                <button onClick={goBack} className="hover:text-teal"><ChevronLeft className="w-3 h-3 inline" /> Back</button>
                <span className="mx-1">/</span>
                {currentPath.map((p, i) => <span key={i} className="text-slate-500">{p.name}{i < currentPath.length - 1 ? " / " : ""}</span>)}
              </div>

              {loadingItems ? <Loader2 className="w-4 h-4 animate-spin text-teal mx-auto my-4" /> : (
                <div className="space-y-0.5">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-navy-hover text-xs">
                      {item.is_folder ? (
                        <button className="flex items-center gap-2 flex-1 text-slate-300"
                          onClick={() => browseFolder(currentPath[0]?.driveId || selectedMatter?.driveId, item.id, item.name)}>
                          <FolderOpen className="w-3.5 h-3.5 text-status-amber" />{item.name}
                        </button>
                      ) : (
                        <label className="flex items-center gap-2 flex-1 text-slate-300 cursor-pointer">
                          <Checkbox checked={selectedFiles.some(f => f.id === item.id)} onCheckedChange={() => toggleFile(item)} className="border-slate-600" />
                          <FileText className="w-3.5 h-3.5 text-slate-500" />
                          <span className="truncate flex-1">{item.name}</span>
                          <span className="text-[10px] text-slate-500">{item.size > 0 ? (item.size < 1048576 ? (item.size/1024).toFixed(0)+"KB" : (item.size/1048576).toFixed(1)+"MB") : ""}</span>
                        </label>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-center text-slate-500 py-4">Empty folder</div>}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between p-3 border-t border-navy-border">
          <span className="text-xs text-slate-500">{selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-slate-400">Cancel</Button>
            <Button size="sm" onClick={() => { onAttach(selectedFiles); onClose(); }} disabled={selectedFiles.length === 0}
              className="text-xs bg-teal hover:bg-teal text-white" data-testid="attach-files-btn">
              <Paperclip className="w-3 h-3 mr-1" /> Attach {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// COMPOSE PANEL
// ═══════════════════════════════════════════════════════════════════
// BM-H4112: one "Record time" box for every touchpoint screen.
const _composeRecipientLabel = (to) => (to || "").split(",")[0].trim();

export function ComposePanel({ bonsaiApi, onClose, onSent, mode = "new", replyTo = null, defaultFileMatter = null }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [timeBox, setTimeBox] = useState(EMPTY_TIME);
  const [showSigSettings, setShowSigSettings] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [spAttachments, setSpAttachments] = useState([]);
  const [deviceFiles, setDeviceFiles] = useState([]); // [{file: File, id: string}]
  const [sigPreviewHtml, setSigPreviewHtml] = useState("");
  const bodyRef = useRef(null);
  const deviceFileRef = useRef(null);
  const headers = (() => { const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id"); return uid ? { "X-Bonsai-User-Id": uid } : {}; })();

  // ── File on Send state ─────────────────────────────────────────
  const [fileOnSend, setFileOnSend] = useState(
    (mode === "reply" || mode === "replyAll" || mode === "forward") && !!defaultFileMatter?.driveId
  );
  const [fileMatterSearch, setFileMatterSearch] = useState("");
  const [fileMatterResults, setFileMatterResults] = useState([]);
  const [selectedFileMatter, setSelectedFileMatter] = useState(null);

  // Pre-populate file-on-send matter from the filing sidebar's current selection
  useEffect(() => {
    if (defaultFileMatter?.driveId && !selectedFileMatter) {
      setSelectedFileMatter({
        driveId: defaultFileMatter.driveId,
        itemId: defaultFileMatter.itemId || defaultFileMatter.folderId,
        reference: defaultFileMatter.reference || defaultFileMatter.title,
        title: defaultFileMatter.title || defaultFileMatter.reference,
      });
    }
  }, [defaultFileMatter]);

  // AI auto-match: if replying/forwarding and no matter pre-selected, ask AI for the best match
  useEffect(() => {
    if (!replyTo?.id) return;
    if (selectedFileMatter) return; // already have a match
    if (!(mode === "reply" || mode === "replyAll" || mode === "forward")) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/bonsai/mail/messages/${encodeURIComponent(replyTo.id)}/suggest`, { headers });
        if (cancelled) return;
        const aiRef = res.data?.ai_suggestion?.matter_reference;
        const histRef = res.data?.history_match?.matter_reference;
        const ref = aiRef || histRef;
        if (!ref) return;
        // Search for the matter to get driveId/itemId
        const mRes = await axios.get(`${API}/bonsai/filing/matters`, { params: { q: ref }, headers });
        if (cancelled) return;
        const matters = Array.isArray(mRes.data) ? mRes.data : mRes.data.matters || [];
        const match = matters.find(m => (m.reference || "").toLowerCase() === ref.toLowerCase()) || matters[0];
        if (match) {
          setSelectedFileMatter(match);
          setFileOnSend(true);
          toast.success(`AI matched: ${match.reference || match.title} — will file on send`);
        }
      } catch { /* AI suggestion not critical */ }
    })();
    return () => { cancelled = true; };
  }, [replyTo?.id, mode]);
  const [fileFavorites, setFileFavorites] = useState([]);

  const SUBFOLDERS = []; // Not used — always files to Attendances

  // Load favourites and signature on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/bonsai/filing/favorites`, { headers });
        setFileFavorites(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* ignore */ }
    })();
    // Load signature to show preview in compose body
    (async () => {
      try {
        const res = await axios.get(`${API}/bonsai/mail/signature`, { headers });
        const sig = res.data;
        if (sig && (sig.image_base64 || sig.text_name)) {
          let sigHtml = '<div style="margin-top:24px;font-family:Calibri,sans-serif;font-size:11pt;color:#000">';
          sigHtml += '<p style="margin:0">Kind regards,</p>';
          if (sig.image_base64) {
            sigHtml += `<img src="${sig.image_base64}" alt="Signature" style="max-width:400px;margin:8px 0" />`;
          } else {
            if (sig.text_name) sigHtml += `<p style="margin:4px 0;font-weight:bold">${sig.text_name}</p>`;
            if (sig.text_title) sigHtml += `<p style="margin:2px 0;color:#555">${sig.text_title}</p>`;
          }
          if (sig.disclaimer) sigHtml += `<p style="margin:8px 0;font-size:8pt;color:#888">${sig.disclaimer}</p>`;
          sigHtml += '</div>';
          setSigPreviewHtml(sigHtml);
        }
      } catch (e) { /* sig preview not critical */ }
    })();
  }, []);

  // Search matters for filing
  useEffect(() => {
    if (fileMatterSearch.length < 2) { setFileMatterResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/bonsai/filing/matters`, { params: { q: fileMatterSearch }, headers });
        setFileMatterResults(Array.isArray(res.data) ? res.data : res.data.matters || []);
      } catch (e) { console.error(e); }
    }, 350);
    return () => clearTimeout(t);
  }, [fileMatterSearch]);

  const selectFileMatter = (matter) => {
    setSelectedFileMatter(matter);
    setFileMatterSearch("");
    setFileMatterResults([]);
  };

  // Pre-fill for reply/forward
  useEffect(() => {
    if (!replyTo) return;
    if (mode === "reply") {
      setTo(replyTo.from_email || "");
      setSubject(replyTo.subject?.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`);
      setBody(`<br/><br/><hr style="border:none;border-top:1px solid #444;margin:16px 0"/><div style="color:#888;font-size:12px;"><b>From:</b> ${replyTo.from_name} &lt;${replyTo.from_email}&gt;<br/><b>Sent:</b> ${new Date(replyTo.received).toLocaleString()}<br/><b>Subject:</b> ${replyTo.subject}</div><br/>${replyTo.body_html || ""}`);
    } else if (mode === "replyAll") {
      const allTo = [replyTo.from_email, ...(replyTo.to || []).map(t => t.email)].filter(Boolean);
      setTo(allTo.join(", "));
      if (replyTo.cc?.length) setCc(replyTo.cc.map(c => c.email).join(", "));
      setShowCc(!!replyTo.cc?.length);
      setSubject(replyTo.subject?.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`);
      setBody(`<br/><br/><hr style="border:none;border-top:1px solid #444;margin:16px 0"/><div style="color:#888;font-size:12px;"><b>From:</b> ${replyTo.from_name} &lt;${replyTo.from_email}&gt;<br/><b>Sent:</b> ${new Date(replyTo.received).toLocaleString()}<br/><b>Subject:</b> ${replyTo.subject}</div><br/>${replyTo.body_html || ""}`);
    } else if (mode === "forward") {
      setSubject(replyTo.subject?.startsWith("Fwd:") ? replyTo.subject : `Fwd: ${replyTo.subject}`);
      setBody(`<br/><br/><hr style="border:none;border-top:1px solid #444;margin:16px 0"/><div style="color:#888;font-size:12px;"><b>From:</b> ${replyTo.from_name} &lt;${replyTo.from_email}&gt;<br/><b>To:</b> ${(replyTo.to || []).map(t => t.email).join(", ")}<br/><b>Sent:</b> ${new Date(replyTo.received).toLocaleString()}<br/><b>Subject:</b> ${replyTo.subject}</div><br/>${replyTo.body_html || ""}`);
    }
  }, [replyTo, mode]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error("Please enter a recipient"); return; }
    if (!subject.trim()) { toast.error("Please enter a subject"); return; }
    const timeOn = fileOnSend && selectedFileMatter && timeBox.enabled;
    if (timeOn && timeFieldProblem(timeBox)) { toast.error(timeFieldProblem(timeBox)); return; }
    setSending(true);
    try {
      const composedBody = bodyRef.current?.innerHTML || body;
      const payload = {
        to: to.split(",").map(s => s.trim()).filter(Boolean),
        cc: cc ? cc.split(",").map(s => s.trim()).filter(Boolean) : [],
        bcc: bcc ? bcc.split(",").map(s => s.trim()).filter(Boolean) : [],
        subject,
        body_html: composedBody,
        sharepoint_attachments: spAttachments.map(f => ({ driveId: f.driveId, itemId: f.id, name: f.name })),
      };
      if (mode === "reply" || mode === "replyAll") {
        payload.reply_to_id = replyTo?.id;
        payload.reply_all = mode === "replyAll";
      }
      if (mode === "forward") payload.forward_id = replyTo?.id;
      if (mode === "reply" || mode === "replyAll" || mode === "forward") {
        // BM-H114: this editor already embedded the quoted original above, and
        // the user may have trimmed it. Tell the server not to prepend Graph's
        // own copy on top — composedBody is the whole message.
        payload.quote_original = false;
      }

      // Add file-on-send if enabled and a matter is selected
      if (fileOnSend && selectedFileMatter) {
        payload.file_on_send = {
          matter_id: selectedFileMatter.id || null,
          drive_id: selectedFileMatter.driveId || null,
          folder_id: selectedFileMatter.itemId || null,
          subfolder: "Attendances",
          sharepoint_path: selectedFileMatter.sharePointUrl || selectedFileMatter.sharepoint_url || null,
          include_attachments: false,
        };
      }

      let res;
      if (deviceFiles.length > 0) {
        // Use multipart form data endpoint when device files are attached
        const formData = new FormData();
        formData.append("payload", JSON.stringify(payload));
        deviceFiles.forEach(df => formData.append("attachments", df.file));
        res = await axios.post(`${API}/bonsai/mail/send-with-attachments`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${API}/bonsai/mail/send`, payload, { headers });
      }

      const filed = res.data?.filed;
      if (filed?.success) {
        toast.success(`Email sent & filed to SharePoint`);
      } else if (fileOnSend && selectedFileMatter && !filed?.success) {
        toast.success("Email sent!");
        toast.error(filed?.error || "Filing to SharePoint failed — please file manually");
      } else {
        toast.success("Email sent!");
      }
      // BM-H4112: time for this email, to CaseMinder's time record. The email
      // has gone either way; a refusal (CCL not agreed yet) is said plainly.
      if (timeOn) {
        const t = await recordTouchpointTime({
          value: timeBox, matterRef: selectedFileMatter.reference,
          source: "email", headers,
        });
        if (t.recorded) toast.success("Time recorded");
        else if (t.error) toast.error(`Email sent, but time not recorded: ${t.error}`);
      }
      onSent?.();
      onClose();
    } catch (e) { toast.error(errorMessage(e, "Failed to send")); }
    finally { setSending(false); }
  };

  const removeSpAttachment = (id) => setSpAttachments(prev => prev.filter(f => f.id !== id));
  const removeDeviceFile = (id) => setDeviceFiles(prev => prev.filter(f => f.id !== id));

  const handleDeviceFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const newFiles = files.map(file => ({ file, id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`, name: file.name, size: file.size }));
    setDeviceFiles(prev => [...prev, ...newFiles]);
    // Reset input so the same file can be re-selected
    if (deviceFileRef.current) deviceFileRef.current.value = "";
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / 1048576).toFixed(1)}MB`;
  };

  const totalAttachments = spAttachments.length + deviceFiles.length;

  const modeLabel = { new: "New Email", reply: "Reply", replyAll: "Reply All", forward: "Forward" }[mode];
  const ModeIcon = { new: Send, reply: Reply, replyAll: ReplyAll, forward: Forward }[mode];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="compose-panel">
        <div className="bg-navy border border-navy-border rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-navy-border">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <ModeIcon className="w-4 h-4 text-teal" /> {modeLabel}
            </h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setShowSigSettings(true)} className="h-7 px-2 text-slate-400 text-xs" title="Edit your email signature" data-testid="sig-settings-btn">
                <Image className="w-3 h-3 mr-1" /> Edit Sig
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0 text-slate-400"><X className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Fields */}
          <div className="p-3 space-y-2 border-b border-navy-border">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 w-8">To</label>
              <Input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com"
                className="h-7 text-xs border-navy-border flex-1" data-testid="compose-to" />
              <button onClick={() => setShowCc(!showCc)} className="text-[10px] text-slate-500 hover:text-teal">Cc</button>
              <button onClick={() => setShowBcc(!showBcc)} className="text-[10px] text-slate-500 hover:text-teal">Bcc</button>
            </div>
            {showCc && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 w-8">Cc</label>
                <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@example.com" className="h-7 text-xs border-navy-border flex-1" data-testid="compose-cc" />
              </div>
            )}
            {showBcc && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 w-8">Bcc</label>
                <Input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="bcc@example.com" className="h-7 text-xs border-navy-border flex-1" data-testid="compose-bcc" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 w-8">Subj</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
                className="h-7 text-xs border-navy-border flex-1" data-testid="compose-subject" />
            </div>
          </div>

          {/* ── Brief B1 (mount-orphans-r1) — Bonsai Mail Assist ───────────
              Collapsible draft-with-AI panel between recipients/subject
              area and the body editor. Disabled when there is no matter
              context (selectedFileMatter null) — the assist endpoint
              requires a submission_id; offering a disabled affordance is
              cleaner than hiding the panel entirely. Component owns its
              own loading / copy-to-clipboard / error states. */}
          <details className="border-t border-navy-border" data-testid="mail-assist-collapsible">
            <summary
              className="cursor-pointer list-none px-3 py-2 text-xs text-slate-600 hover:bg-navy-hover/40 flex items-center justify-between"
              data-testid="mail-assist-summary">
              <span>
                Draft with AI
                {!selectedFileMatter?.id && (
                  <span className="ml-2 text-slate-400" data-testid="mail-assist-disabled-hint">
                    — select a matter to enable
                  </span>
                )}
              </span>
              <span className="text-slate-400">{selectedFileMatter?.id ? "expand" : "disabled"}</span>
            </summary>
            {selectedFileMatter?.id && (
              <div className="px-3 pb-3" data-testid="mail-assist-panel">
                <BonsaiMailAssist
                  submissionId={selectedFileMatter.id}
                  recipientName={(to || "").split("@")[0] || ""}
                  context={subject || ""}
                  data-testid="bonsai-mail-assist"
                />
              </div>
            )}
          </details>

          {/* Body editor */}
          <div className="flex-1 min-h-[200px] overflow-y-auto">
            <div
              ref={bodyRef}
              contentEditable
              suppressContentEditableWarning
              className="p-3 min-h-[120px] focus:outline-none"
              style={{ fontFamily: "Calibri, sans-serif", fontSize: "11pt", color: "#000", lineHeight: "1.15", background: "#fff" }}
              dangerouslySetInnerHTML={{ __html: mode === "new" ? "" : DOMPurify.sanitize(body) }}
              data-testid="compose-body"
            />
            {/* Signature preview — auto-applied by server, shown here so user sees it */}
            {sigPreviewHtml && (
              <div className="px-3 pb-3 pointer-events-none" style={{ background: "#fff" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sigPreviewHtml) }}
                data-testid="sig-preview"
              />
            )}
          </div>

          {/* Attachments bar */}
          {totalAttachments > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t border-navy-border">
              {deviceFiles.map(f => (
                <Badge key={f.id} variant="outline" className="text-[10px] border-blue-800 text-blue-400 gap-1" data-testid={`device-att-${f.id}`}>
                  <span title="From device">📎</span>
                  <FileText className="w-2.5 h-2.5" />
                  <span className="max-w-[120px] truncate">{f.name}</span>
                  {f.size > 0 && <span className="text-[9px] text-slate-500">{formatFileSize(f.size)}</span>}
                  <button onClick={() => removeDeviceFile(f.id)} title="Remove"><X className="w-2.5 h-2.5 text-slate-500 hover:text-status-red" /></button>
                </Badge>
              ))}
              {spAttachments.map(f => (
                <Badge key={f.id} variant="outline" className="text-[10px] border-teal text-teal gap-1" data-testid={`sp-att-${f.id}`}>
                  <span title="From SharePoint">🗂️</span>
                  <FileText className="w-2.5 h-2.5" />
                  <span className="max-w-[120px] truncate">{f.name}</span>
                  {f.size > 0 && <span className="text-[9px] text-slate-500">{formatFileSize(f.size)}</span>}
                  <button onClick={() => removeSpAttachment(f.id)} title="Remove"><X className="w-2.5 h-2.5 text-slate-500 hover:text-status-red" /></button>
                </Badge>
              ))}
            </div>
          )}

          {/* ═══ FILE ON SEND PANEL ═══ */}
          <div className="border-t border-navy-border">
            <button
              onClick={() => setFileOnSend(!fileOnSend)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                fileOnSend ? "bg-teal-dim/40 text-teal" : "text-slate-400 hover:text-slate-300 hover:bg-navy"
              }`}
              data-testid="file-on-send-toggle"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              <span className="font-medium">File to SharePoint on Send</span>
              {fileOnSend && (
                <span className="text-[10px] text-teal ml-1">→ Attendances</span>
              )}
              {selectedFileMatter && fileOnSend && (
                <Badge variant="outline" className="text-[10px] border-teal text-teal ml-auto">
                  {selectedFileMatter.reference || selectedFileMatter.title || selectedFileMatter.matter_title}
                </Badge>
              )}
            </button>

            {fileOnSend && (
              <div className="px-3 pb-3 space-y-2 bg-navy" data-testid="file-on-send-panel">
                {/* Favourites (quick picks) */}
                {fileFavorites.length > 0 && !selectedFileMatter && (
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Favourites</label>
                    <div className="flex flex-wrap gap-1">
                      {fileFavorites.map(fav => (
                        <button
                          key={fav.id}
                          onClick={() => selectFileMatter({
                            id: fav.matter_id,
                            reference: fav.matter_reference || fav.folder_name,
                            title: fav.folder_name,
                            driveId: fav.drive_id,
                            itemId: fav.folder_id,
                            sharePointUrl: fav.web_url,
                          })}
                          className="px-2 py-1 rounded text-[10px] bg-navy-hover text-status-amber hover:bg-navy-hover border border-amber-900/30 transition-colors"
                          data-testid={`fav-matter-${fav.id}`}
                        >
                          {fav.matter_reference || fav.folder_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Matter search / selected matter */}
                {!selectedFileMatter ? (
                  <div>
                    <Input
                      placeholder="Search matter by reference or client..."
                      value={fileMatterSearch}
                      onChange={e => setFileMatterSearch(e.target.value)}
                      className="h-7 text-xs border-navy-border"
                      data-testid="file-matter-search"
                    />
                    {fileMatterResults.length > 0 && (
                      <div className="mt-1 max-h-32 overflow-y-auto rounded border border-navy-border bg-navy">
                        {fileMatterResults.slice(0, 8).map(m => (
                          <button
                            key={m.id || m.itemId}
                            onClick={() => selectFileMatter(m)}
                            className="w-full text-left px-2 py-1.5 text-xs text-slate-300 hover:bg-navy-hover border-b border-navy-border last:border-0"
                            data-testid={`file-matter-option-${m.id || m.itemId}`}
                          >
                            <span className="font-medium text-teal">{m.reference || m.title || m.matter_title}</span>
                            {m.client_name && <span className="text-slate-500 ml-2">{m.client_name}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs border-teal text-teal gap-1">
                      <FolderOpen className="w-3 h-3" />
                      {selectedFileMatter.reference || selectedFileMatter.title || selectedFileMatter.matter_title}
                    </Badge>
                    <button onClick={() => { setSelectedFileMatter(null); }}
                      className="text-[10px] text-status-red hover:text-status-red">change</button>
                  </div>
                )}
                {selectedFileMatter && (
                  <RecordTimeField
                    value={timeBox}
                    onChange={setTimeBox}
                    defaultNarrative={_composeRecipientLabel(to) ? `Email to ${_composeRecipientLabel(to)}` : "Email"}
                    testIdPrefix="compose-time"
                  />
                )}

                {/* Show filing target */}
                {selectedFileMatter && (
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <FolderOpen className="w-3 h-3 text-teal" />
                    <span>Will be saved to <span className="text-teal font-medium">Attendances</span> subfolder</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hidden file input for device attachments */}
          <input ref={deviceFileRef} type="file" multiple onChange={handleDeviceFiles} className="hidden" data-testid="device-file-input" />

          {/* Toolbar */}
          <div className="flex items-center justify-between p-3 border-t border-navy-border">
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => deviceFileRef.current?.click()}
                className="text-xs border-blue-800 text-blue-400 hover:bg-blue-900/20" data-testid="attach-device-btn"
                title="Attach file from your device">
                <Paperclip className="w-3 h-3 mr-1" /> Attach File
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowFilePicker(true)}
                className="text-xs border-teal text-teal hover:bg-teal-dim/20" data-testid="attach-sp-btn"
                title="Attach from matter's SharePoint folder">
                <span className="mr-1">📁</span> Attach from matter
              </Button>
              {totalAttachments > 0 && (
                <span className="text-[10px] text-slate-500 self-center ml-1">{totalAttachments} file{totalAttachments !== 1 ? "s" : ""}</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-slate-400" data-testid="discard-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Discard
              </Button>
              <Button size="sm" onClick={handleSend} disabled={sending}
                className={`text-xs px-4 ${fileOnSend && selectedFileMatter ? "bg-status-amber hover:bg-status-amber" : "bg-teal hover:bg-teal"} text-white`}
                data-testid="send-email-btn">
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                {fileOnSend && selectedFileMatter ? "Send & File" : "Send"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {showSigSettings && <SignatureSettings bonsaiApi={bonsaiApi} onClose={() => setShowSigSettings(false)} />}
      {showFilePicker && <SharePointFilePicker bonsaiApi={bonsaiApi} defaultMatter={defaultFileMatter} onAttach={files => setSpAttachments(prev => [...prev, ...files])} onClose={() => setShowFilePicker(false)} />}
    </>
  );
}
